<template>
  <div class="expression-template-input">
    <div class="expression-template-input__header">
      <template v-if="selectedTemplate">
        <input-dropdown
          :value="selectedTemplate.code"
          :options="templateCodes"
          :clear-on-select="false"
          class="expression-template-input__chooser"
          @update:value="selectTemplate($event)"
        >
          <template #default="{ item: code }">
            {{ $t(`expressionTemplates.${code}`) }}
          </template>
        </input-dropdown>
        <button class="button-icon" type="button" @click="selectTemplate(null)">
          <lucide-icon name="x" />
        </button>
      </template>
      <div v-else class="expression-template-input__options">
        <button
          v-for="template in expressionTemplates"
          :key="template.code"
          class="button-pill"
          type="button"
          @click="selectTemplate(template.code)"
        >
        {{ $t(`expressionTemplates.${template.code}`) }}
        </button>
      </div>
    </div>
    <div class="expression-template-input__body" v-if="selectedTemplate" ref="bodyElement">
      <transition-group-list>
        <expression-template-input-line
          v-for="(arg, index) of templateArgs" :key="index"
          :arg="arg"
          :factored="selectedTemplate.factored"
          :expression-parse-context="expressionParseContext"
          @remove="removeTemplateArg(index)"
          @replace="replaceTemplateArg(index, $event)"
          @update:arg="updateTemplateArg(index, $event)"
        />

        <div class="expression-template-input__placeholderline" :key="'after'">
          <button
            v-if="selectedTemplate.maxArgs == null || templateArgs.length < selectedTemplate.maxArgs"
            class="expression-template-input__placeholder"
            type="button"
            @click="openItemPopover"
          ></button>
          <button
            v-if="editor != 'none'"
            type="button"
            class="button-icon expression-template-input__expand"
            @pointerdown.prevent=""
            @click="openEditorPopup()"
          >
            <lucide-icon name="maximize-2" />
          </button>
  
          <inline-popover
            v-if="elementsPopover.visible"
            :left="elementsPopover.left"
            :top="elementsPopover.top"
            :items="elementsOptions"
            marker=""
            @select="addItemReference"
            @close="closeItemPopover"
          >
            <template #default="{ item }">
              <span v-if="item?.title">
                {{ item.title }}
              </span>
              <span v-else class="placeholder-text">
                {{ $t("labels.unnamed") }}
              </span>
            </template>
          </inline-popover>
        </div>
      </transition-group-list>

    </div>

    <expression-editor
      v-if="editor === 'inbuilt'"
      :visible="expressionEditorVisible"
      :tree="tree"
      :expression="expression"
      :parse-context="expressionParseContext"
      :input-mode="'template'"
      @update:expression="emit('update:expression', $event)"
      @update:input-mode="emit('update:input-mode', $event)"
      @close="closeEditor()"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { clampNumber } from "@/helpers/utility/MathHelper";

import InputDropdown from "@core/input/InputDropdown.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ExpressionTemplateInputLine from "./ExpressionTemplateInputLine.vue";
import ExpressionEditor from "./ExpressionEditor.vue";
import InlinePopover from "@core/input/InlinePopover.vue";
import TransitionGroupList from "@core/wrappers/TransitionGroupList.vue";

import { useFeatures } from "@editor/store/features.store";
import { useExpressionMenu } from "./useExpressionMenu";
import {
  templateCodes,
  expressionTemplates,
  encodeTemplateExpression,
  parseTemplateExpression
} from "./expressionTemplates";

import type { TemplateArg, TemplateCode, TemplateGroupArg } from "./expressionTemplates";
import type { ExpressionTree } from "./ExpressionTree";
import type { ExpressionParseContext } from "../effect/Expression";

const props = withDefaults(defineProps<{
  expression: string;
  tree: ExpressionTree;
  expressionParseContext: ExpressionParseContext;
  editor?: "inbuilt" | "external" | "none";
  editorOpen?: boolean;
}>(), {
  editor: "none",
  editorOpen: false
});

const emit = defineEmits<{
  (e: "update:expression", expression: string): void;
  (e: "update:input-mode", value: "expression" | "template" | "tree"): void;
  (e: "update:editor-open", value: boolean): void;
}>();

const bodyElement = ref<HTMLElement | null>(null);

const features = useFeatures();
const { t } = useI18n();

const {
  elements
} = useExpressionMenu();

const elementsOptions = computed(() => {
  const options: { uid: string; type: "target" | "resource" | "index", id: number, title: string }[] = [...elements.value];

  const allTitles = {
    target: t("labels.allTargets"),
    resource: t("labels.allResources"),
    index: t("labels.allIndices")
  };
  const allValues = {
    target: "all_targets",
    resource: "all_resources",
    index: "all_indices"
  };

  const allOptions: string[] = [];
  for (const option of options) {
    if (allOptions.includes(option.type)) continue;
    if (option.type == "index") continue; // don't allow to add all indices (this will lead to infinite recursion)
    if (!features[option.type]) continue;
    allOptions.push(option.type);
    options.splice(options.indexOf(option), 0, {
      type: option.type,
      uid: allValues[option.type],
      id: -allOptions.length,
      title: allTitles[option.type]
    });
  }

  return options;
});

const selectedTemplate = computed(() => {
  if (!props.expression.startsWith("tmpl_")) return null;
  const templateCodeWithPrefix = props.expression.split(":")[0];
  const templateCode = templateCodeWithPrefix.substring(5);
  if (!(templateCode in expressionTemplates)) {
    console.error(`Template ${templateCode} does not exist!`);
    return null;
  }
  return expressionTemplates[templateCode as TemplateCode];
});

const templateArgs = ref<TemplateArg[]>(parseTemplateExpression(props.expression).args);

const lastTemplateExpression = ref(props.expression);

watch(templateArgs, (value) => {
  if (!selectedTemplate.value) return;
  const expression = encodeTemplateExpression(selectedTemplate.value.code, value);
  if (expression === props.expression) return;
  props.tree.setExpression(expression);
  emit("update:expression", expression);
}, { deep: true });

watch(() => props.expression, (expression) => {
  templateArgs.value = parseTemplateExpression(expression).args;
}, { immediate: true });

function selectTemplate(templateCode: TemplateCode | null) {
  if (templateCode == null) {
    lastTemplateExpression.value = "";
    emit("update:expression", "");
    templateArgs.value = [];
    return;
  }

  // If the template is already selected, do nothing
  if (templateCode == selectedTemplate.value?.code) return;

  const expression = encodeTemplateExpression(templateCode, []);
  props.tree.setExpression(expression);
  emit("update:expression", expression);
}

function addTemplateArg(type: "variable" | "item" | "number", value: string) {
  let factor: number | undefined = undefined;
  if (selectedTemplate.value?.factored) {
    factor = 1;
  }
  templateArgs.value.push({ type, value, factor });

  scrollToEnd();
}
function addTemplateGroupArg(value: TemplateGroupArg["value"]) {
  templateArgs.value.push({ type: "group", value, factors: {} });

  scrollToEnd();
}
function removeTemplateArg(index: number) {
  templateArgs.value.splice(index, 1);
}
function replaceTemplateArg(index: number, args: TemplateArg[]) {
  templateArgs.value.splice(index, 1, ...args);
}
function updateTemplateArg(index: number, arg: TemplateArg) {
  templateArgs.value[index] = arg;
}

function scrollToEnd() {
  nextTick(() => {
    if (bodyElement.value) {
      bodyElement.value.scrollTop = bodyElement.value.scrollHeight;
    }
  });
}


// =============================================
// EXPRESSION EDITOR POPUP

const expressionEditorVisible = ref(false);
watch(() => props.editorOpen, (value) => {
  expressionEditorVisible.value = value;
});

function openEditorPopup() {
  if (props.editor === "none") return;
  if (props.editor === "external") {
    emit("update:editor-open", true);
    return;
  }
  expressionEditorVisible.value = true;
}

function closeEditor() {
  if (props.editor === "none") return;
  if (props.editor === "external") {
    emit("update:editor-open", false);
    return;
  }
  expressionEditorVisible.value = false;
}



// =============================================
// ITEM POPOVER

function addItemReference(item: { uid: string, type: "target" | "resource" | "index" }) {
  if (item.uid.startsWith("all_")) {
    addTemplateGroupArg(item.uid as TemplateGroupArg["value"]);
  } else {
    addTemplateArg("item", item.uid);
  }
}

const elementsPopover = reactive({
  visible: false,
  top: 0,
  left: 0
});

function openItemPopover(event: MouseEvent) {
  const target = event.target as HTMLElement;
  const rect = target.getBoundingClientRect();
  const left = clampNumber(event.clientX - 20, rect.left, rect.right - 50);
  const top = rect.top + 7;

  elementsPopover.top = top;
  elementsPopover.left = left;
  elementsPopover.visible = true;
}

function closeItemPopover() {
  elementsPopover.visible = false;
}


defineExpose({
  addTemplateArg,
  addTemplateGroupArg,
  removeTemplateArg
});
</script>

<style lang="scss">
.expression-template-input {
  display: grid;
  grid-template-rows: min-content minmax(0, 1fr);
  grid-template-columns: minmax(0, 1fr);
  max-height: 100%;
  max-width: 100%;

  &__header {
    max-width: 100%;
    min-height: 3.5rem;
    padding-bottom: 0.5rem;
    padding-left: 0.5rem;
    display: flex;
    align-items: center;
    margin-left: -0.5rem;
  }
  &__options {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  &__chooser {
    width: 20rem;
    max-width: 100%;
  }
  &__body {
    margin-left: -0.5rem;
    overflow-x: hidden;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-left: 0.5rem;
    padding-bottom: 1rem;
    padding-top: 0.75rem;
    padding-right: 0.5rem;
    margin-right: -0.5rem;
    box-shadow: inset 0 -2px 2px -2px var(--color-border), inset 0 2px 2px -2px var(--color-border);
  }
  &__placeholder {
    height: 2.2rem;
    width: 20rem;
    max-width: 100%;
    border: 2px dashed var(--color-border);
    border-radius: 1rem;
    background-color: rgba(var(--tint-contrast), 0.025);
  }
  &__placeholderline {
    display: flex;
    gap: 0.5rem;
    max-width: 100%;
    flex-shrink: 0;
    padding-top: 0.25rem;
  }

  &__expand {
    flex-shrink: 0;
    padding-block: 0.5rem;
  }
}
</style>